import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FUSION_CONTACT } from '../lib/fusionContact';

// Help & Contact tab inside the supplier-portal slide-out. Two
// contact-action rows up top (email + phone, native handlers), then
// a Common Topics list that links into the standalone help article
// routes. Clicking a topic closes the slide-out and navigates.
//
// onClose comes from the slide-out so we can dismiss before
// navigating – keeps the back-button experience clean (the article
// page becomes the only thing on screen, no slide-out hanging around
// from history).

// Slim wrapper around a clickable card row. Style matches the
// Compliance Overview supplier-header pattern (panel bg, accent
// border, hover lifts the border to fusion green).
const ContactAction = ({ icon, label, value, href, onClick }) => (
  <a
    href={href}
    onClick={onClick}
    className="flex items-center gap-3 px-4 py-3.5 bg-white border border-[#E6EADB] rounded-xl shadow-lift no-underline mb-3 transition-[transform,box-shadow] hover:shadow-lift-hover hover:-translate-y-0.5 group">
    <span className="w-[34px] h-[34px] rounded-lg text-white flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #92D400, #009159)' }}>
      {icon}
    </span>
    <span className="flex-1 min-w-0">
      <span className="block font-['Metropolis'] text-[9px] font-bold tracking-[0.2em] uppercase text-[#51534A] mb-0.5">
        {label}
      </span>
      <span className="block font-['Metropolis'] text-[13px] font-semibold text-ink break-all">
        {value}
      </span>
    </span>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"
      className="text-[#51534A] flex-shrink-0 group-hover:text-active-green transition-colors">
      <path d="M7 17l9.2-9.2M17 17V7H7" />
    </svg>
  </a>
);

// One row in the Common Topics list. Icon, title, sub-line, chevron.
// onClick closes the slide-out before navigating so the supplier
// lands on the article page with a clean stack.
const HelpTopic = ({ icon, title, sub, route, onNavigate }) => (
  <button
    type="button"
    onClick={() => onNavigate(route)}
    className="w-full text-left flex gap-3.5 items-center p-3.5 mb-2.5 bg-white border border-[#E6EADB] rounded-xl shadow-lift cursor-pointer hover:shadow-lift-hover hover:-translate-y-0.5 transition-[transform,box-shadow] font-['Metropolis']">
    <span className="w-8 h-8 rounded-lg text-white flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #B4E2A5, #009159)' }}>
      {icon}
    </span>
    <span className="flex-1 min-w-0">
      <span className="block text-[13px] text-ink font-bold mb-0.5">{title}</span>
      <span className="block text-[11px] text-[#51534A] leading-relaxed">{sub}</span>
    </span>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"
      className="text-[#51534A] flex-shrink-0 mt-2">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  </button>
);

export default function SupplierHelpContactTab({ onClose }) {
  const navigate = useNavigate();
  const goToArticle = (route) => {
    onClose?.();
    navigate(route);
  };

  return (
    <div className="font-['Metropolis']">

      {/* Intro line – sets expectations on response time so suppliers
          aren't left wondering whether their email vanished. */}
      <p className="text-[13px] text-fusion-mid-grey leading-relaxed mb-5">
        For anything urgent give us a call. For everything else drop us an email – we'll get back to you as soon as possible.
      </p>

      {/* Email + Phone – native handlers via mailto: / tel:. The phone
          tel: target uses the international-format constant so the
          dial works regardless of the caller's region. */}
      <ContactAction
        icon={
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
        }
        label="Email Us"
        value={FUSION_CONTACT.email}
        href={`mailto:${FUSION_CONTACT.email}`} />

      <ContactAction
        icon={
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
          </svg>
        }
        label={`Call Us · ${FUSION_CONTACT.hours}`}
        value={FUSION_CONTACT.phoneDisplay}
        href={`tel:${FUSION_CONTACT.phoneFormatted}`} />

      {/* Common Topics – 4 articles, each living at its own /help/*
          route. The list is small + curated rather than searchable on
          purpose: when the supplier base grows the search bar can come
          back as a separate piece of work. */}
      <div className="mt-7">
        <div className="font-['Metropolis'] text-[10px] font-bold tracking-[0.25em] uppercase text-[#009159] mb-3.5 pb-2.5 border-b border-line">
          Common Topics
        </div>

        <HelpTopic
          icon={
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <polyline points="9 12 11 14 15 10" />
            </svg>
          }
          title="What does my compliance status mean?"
          sub="Green, Amber and Red explained – and how it affects being awarded work."
          route="/help/compliance"
          onNavigate={goToArticle} />

        <HelpTopic
          icon={
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 15V4" />
              <polyline points="7 9 12 4 17 9" />
              <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
            </svg>
          }
          title="How do I upload a compliance document?"
          sub="Step-by-step guide to uploading insurance, H&S policies and certificates."
          route="/help/upload-compliance"
          onNavigate={goToArticle} />

        <HelpTopic
          icon={
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="9" y1="13" x2="15" y2="13" />
              <line x1="9" y1="17" x2="15" y2="17" />
            </svg>
          }
          title="How do briefs work?"
          sub="From receiving a brief to submitting your response and getting awarded."
          route="/help/how-briefs-work"
          onNavigate={goToArticle} />

        <HelpTopic
          icon={
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <line x1="12" y1="8" x2="12" y2="14" />
              <line x1="9" y1="11" x2="15" y2="11" />
            </svg>
          }
          title="How do recognition badges work?"
          sub="Optional recognition like the Eco badge – grow from Acorn to Oak Status."
          route="/help/recognition"
          onNavigate={goToArticle} />
      </div>
    </div>
  );
}
